import { createContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Unit from 'src/constants/unit';

const OVERRIDE_KEYS = {
  [Unit.IMPERIAL]: {
    labels: 'IMPERIAL_LABELS',
    enpis: 'labelsImperial',
    compressor: 'labelsImperial',
    energy: 'labelsImperial',
  },
  [Unit.METRIC_EU]: {
    labels: 'METRIC_EU_LABELS',
    enpis: 'labelsMetricEu',
    compressor: 'labelsMetricEu',
    energy: 'labelsMetricEu',
  },
};

function mergeOverride(base, override) {
  const out = {};
  Object.keys(base).forEach((k) => {
    out[k] = override && override[k] ? override[k] : base[k];
  });
  return out;
}

const UnitContext = createContext({
  unit: Unit.METRIC,
  labels: {},
  electricalLabels: {},
  enpisLabels: {},
  compressorLabels: {},
  energyLabels: {},
  assignmentLabels: {},
  showUnitToggle: false,
  setShowUnitToggle: () => {},
  setUnit: () => {},
  setOffset: () => {},
  offset: {},
});

export function UnitProvider({ children }) {
  const { t } = useTranslation();
  const languageLabels = t('constants.recordFields', { returnObject: true });
  const assignmentLanguageLabels = t('views.assignmentsList.filters', { returnObject: true });
  const electricaLanguagelLabels = t('constants.electricalRecordFields', { returnObject: true });
  const enpisLanguageLabels = t('constants.enpisFields.labels', { returnObject: true });
  const compressorLanguageLabels = t('constants.compressorFields.labels', { returnObject: true });
  const energyLanguageLabels = t('constants.energyFields.labels', { returnObject: true });
  const [unit, setUnit] = useState({ value: Unit.METRIC, changed: false });
  const [showUnitToggle, setShowUnitToggle] = useState(false);
  const [labels, setLabels] = useState(languageLabels);
  const [electricalLabels, setElectricalLabels] = useState({ electricaLanguagelLabels });
  const [enpisLabels, setEnpisLabels] = useState({ enpisLanguageLabels });
  const [compressorLabels, setCompressorLabels] = useState({ compressorLanguageLabels });
  const [energyLabels, setEnergyLabels] = useState({ energyLanguageLabels });
  const [assignmentLabels, setAssignmentLabels] = useState({ assignmentLanguageLabels });
  const [offset, setOffset] = useState({});

  function handleUnitChange(newUnit) {
    setElectricalLabels(electricaLanguagelLabels);
    setAssignmentLabels(assignmentLanguageLabels);

    if (newUnit === Unit.METRIC || !OVERRIDE_KEYS[newUnit]) {
      setLabels(languageLabels);
      setEnpisLabels(enpisLanguageLabels);
      setCompressorLabels(compressorLanguageLabels);
      setEnergyLabels(energyLanguageLabels);
    } else {
      const k = OVERRIDE_KEYS[newUnit];
      const labelsOverride = t(`constants.${k.labels}`, { returnObject: true });
      const enpisOverride = t(`constants.enpisFields.${k.enpis}`, { returnObject: true });
      const compressorOverride = t(`constants.compressorFields.${k.compressor}`, { returnObject: true });
      const energyOverride = t(`constants.energyFields.${k.energy}`, { returnObject: true });

      setLabels(mergeOverride(languageLabels, labelsOverride));
      setEnpisLabels(mergeOverride(enpisLanguageLabels, enpisOverride));
      setCompressorLabels(mergeOverride(compressorLanguageLabels, compressorOverride));
      setEnergyLabels(mergeOverride(energyLanguageLabels, energyOverride));
    }

    setUnit({ value: newUnit, changed: true });
  }

  function handleOffset(key, value) {
    const update = { ...offset };
    update[key] = value;
    setOffset(update);
  }

  useEffect(() => {
    handleUnitChange(unit.value);
  }, [unit.value, t]);

  const contextValue = useMemo(() => ({
    unit: unit.value,
    unitChanged: unit.changed,
    labels,
    electricalLabels,
    enpisLabels,
    compressorLabels,
    energyLabels,
    assignmentLabels,
    setUnit: handleUnitChange,
    showUnitToggle,
    setShowUnitToggle,
    setOffset: handleOffset,
    offset,
  }));

  return <UnitContext.Provider value={contextValue}>{children}</UnitContext.Provider>;
}

UnitProvider.propTypes = {
  children: PropTypes.node,
};

export default UnitContext;
